// UPDOGS: Zeigt in der Produkt-Gallery nur die Bilder der gewählten Variante.
// Gruppe = Variantenbild plus die direkt folgenden Bilder bis zum nächsten Variantenbild;
// Bilder vor dem ersten Variantenbild gelten für alle Varianten.
//
// Prinzip: Horizon ersetzt die Gallery bei jedem Variantenwechsel durch frisch gerendertes
// Server-HTML (media-gallery.js). Dieses Script entfernt die fremden Bilder aus genau diesem
// HTML, BEVOR Horizon es einsetzt – die Slideshow startet dann schon mit der richtigen Liste.
// Kein nachträgliches Ausblenden, keine Korrekturen an laufenden Slideshows.
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** Klick-Aktionen, deren letzte Zahl eine Position in der Bilderliste ist. */
const INDEX_ACTION = /\/(select|zoom|open|handleThumbnailClick)\/(\d+)$/;

class VariantGalleryFilter extends Component {
  /** @type {{ order: number[], anchors: number[], initialAnchor: number | null, slideLabel: string, zoomLabel: string } | null} */
  #config = null;
  /** @type {Map<number, Set<number>>} */
  #groups = new Map();
  /** @type {Set<number>} */
  #shared = new Set();
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#config = this.#readConfig();
    if (!this.#config || this.#config.anchors.length < 2) return;
    this.#buildGroups();

    // Capture auf document: läuft vor dem Listener der media-gallery, daher hängt unser .then
    // zuerst an der Event-Promise und bearbeitet das HTML, bevor die Gallery ersetzt wird.
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      capture: true,
      signal: this.#controller.signal,
    });

    // Erstes Laden: Gallery einmal durch eine gefilterte Kopie ersetzen (wie beim Variantenwechsel).
    const gallery = this.#scope()?.querySelector('media-gallery');
    if (gallery) {
      const copy = /** @type {HTMLElement} */ (gallery.cloneNode(true));
      if (this.#filter(copy, this.#config.initialAnchor)) gallery.replaceWith(copy);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** @param {Event & { promise?: Promise<{ detail?: { html?: Document | Element, resource?: { featured_media?: { id: number } } | null } }> }} event */
  #handleProductSelect = (event) => {
    const { target } = event;
    if (!(target instanceof Element) || target.closest('product-card')) return;
    if (!this.#scope()?.contains(target)) return;

    event.promise
      ?.then(({ detail } = {}) => {
        const gallery = detail?.html?.querySelector?.('media-gallery');
        if (gallery) this.#filter(/** @type {HTMLElement} */ (gallery), detail?.resource?.featured_media?.id ?? null);
      })
      .catch(() => {});
  };

  /** Derselbe Bereich, auf den media-gallery.js hört (Produktseite oder Quick-Add-Dialog). */
  #scope() {
    return this.closest('.shopify-section, dialog');
  }

  #readConfig() {
    try {
      return JSON.parse(this.querySelector('script[type="application/json"]')?.textContent ?? '');
    } catch (error) {
      console.warn('[updogs-gallery] Konfiguration fehlerhaft', error);
      return null;
    }
  }

  #buildGroups() {
    if (!this.#config) return;
    const anchors = new Set(this.#config.anchors);
    /** @type {number | null} */
    let current = null;
    for (const id of this.#config.order) {
      if (anchors.has(id)) {
        current = id;
        this.#groups.set(id, new Set([id]));
      } else if (current === null) {
        this.#shared.add(id);
      } else {
        this.#groups.get(current)?.add(id);
      }
    }
    if (window.Shopify?.designMode) {
      console.debug('[updogs-gallery] Gruppen (Bilder pro Variante im Admin zusammenhängend sortieren):', {
        alleVarianten: [...this.#shared],
        gruppen: [...this.#groups].map(([anchor, ids]) => ({ variantenbild: anchor, bilder: [...ids] })),
      });
    }
  }

  /**
   * Entfernt fremde Bilder aus einer (noch nicht eingesetzten) Gallery und nummeriert alle
   * Bedienelemente neu. Gibt true zurück, wenn etwas entfernt wurde.
   * @param {HTMLElement} gallery
   * @param {number | null} anchor - Variantenbild der gewählten Variante
   */
  #filter(gallery, anchor) {
    const group = anchor == null ? null : this.#groups.get(anchor);
    if (!group || !this.#config) return false;
    const keep = new Set([...group, ...this.#shared]);

    // Reihenfolge der Bilder, wie Horizon sie gerendert hat (jede Position zählt in diese Liste).
    const order = [
      ...new Set(
        [...gallery.querySelectorAll('.product-media[data-media-id]')].map((el) =>
          Number(/** @type {HTMLElement} */ (el).dataset.mediaId)
        )
      ),
    ];
    /** @type {Map<number, number>} alte Position → neue Position */
    const newIndex = new Map();
    order.forEach((id) => {
      if (keep.has(id)) newIndex.set(order.indexOf(id), newIndex.size);
    });
    const count = newIndex.size;
    if (count === order.length || count === 0) return false;

    // 1) Bilder samt Hülle entfernen (Slide, Grid-Eintrag, Zoom-Eintrag).
    for (const el of gallery.querySelectorAll('.product-media[data-media-id]')) {
      if (!keep.has(Number(/** @type {HTMLElement} */ (el).dataset.mediaId))) {
        el.closest('slideshow-slide, li')?.remove();
      }
    }

    // 2) Thumbnails, Punkte, Zoom-Knöpfe: entfernen oder auf die neue Position umschreiben.
    for (const el of gallery.querySelectorAll('[on\\:click]')) {
      const action = el.getAttribute('on:click') ?? '';
      const match = action.match(INDEX_ACTION);
      if (!match) continue;
      const position = newIndex.get(Number(match[2]));
      if (position === undefined) {
        (el.parentElement?.tagName === 'LI' ? el.parentElement : el).remove();
        continue;
      }
      el.setAttribute('on:click', action.replace(INDEX_ACTION, `/${match[1]}/${position}`));
      if (match[1] === 'select') {
        el.setAttribute('aria-label', this.#label(this.#config.slideLabel, position, count));
        if (el.hasAttribute('style')) el.setAttribute('style', el.getAttribute('style').replace(/--slide-\d+/, `--slide-${position}`));
        if (/^\d+$/.test(el.textContent?.trim() ?? '')) el.textContent = String(position + 1);
        if (position === 0) el.setAttribute('aria-selected', 'true');
        else el.removeAttribute('aria-selected');
      } else if (match[1] === 'zoom') {
        el.setAttribute('aria-label', this.#label(this.#config.zoomLabel, position, count));
      }
    }

    // 3) Slideshows: Slides neu nummerieren, Zeitachsen und Zähler anpassen.
    for (const slideshow of gallery.querySelectorAll('slideshow-component')) {
      const slides = [...slideshow.querySelectorAll('slideshow-slide')];
      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== 0));
        const style = slide.getAttribute('style');
        if (style) slide.setAttribute('style', style.replace(/--slide-\d+/, `--slide-${index}`));
      });
      const scope = slides.map((_, index) => `--slide-${index}`).join(',');
      const style = slideshow.getAttribute('style') ?? '';
      slideshow.setAttribute('style', style.replace(/--slideshow-timeline:[^;]*;/, `--slideshow-timeline: ${scope};`));
      slideshow.setAttribute('initial-slide', '0');
      if (slides.length <= 1) {
        slideshow.classList.add('product-media-gallery__slideshow--single-media', 'slideshow--single-media');
        slideshow.querySelectorAll('slideshow-controls, slideshow-arrows').forEach((el) => el.remove());
      }
    }
    for (const counter of gallery.querySelectorAll('.slideshow-controls__counter')) {
      const total = [...counter.childNodes].reverse().find((node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim());
      if (total) total.textContent = String(count);
    }
    return true;
  }

  /** @param {string} template @param {number} position @param {number} count */
  #label(template, position, count) {
    return template.replace('[index]', String(position + 1)).replace('[length]', String(count));
  }
}

if (!customElements.get('updogs-variant-gallery-filter')) {
  customElements.define('updogs-variant-gallery-filter', VariantGalleryFilter);
}
